import { HttpErrorResponse } from '@angular/common/http';

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, first, mergeMap, Observable, of } from 'rxjs';
import { BaseWizardComponent } from '../base-wizard.component';
import { ILotPlanModel, LotPlanModel } from '../models/veg-wizard-models';
import { Logger } from '../services/logger-service';
import { LotPlanService } from '../services/lot-plan.service';
import { VegWizardDataService } from '../services/veg-wizard-data.service';

@UntilDestroy()
@Component({
  selector: 'app-lot-plan',
  templateUrl: './lot-plan.component.html',
  styleUrls: ['./lot-plan.component.css'],
  imports: [
    ReactiveFormsModule
],
})
export class LotPlanComponent extends BaseWizardComponent implements OnInit {

  form: FormGroup;
  lotPlanData: ILotPlanModel;

  isVerifyClicked = false;
  isLoading = false;
  allowToGoNextScreen = false;
  showLotPlanInformation = false;

  constructor(
    private formBuilder: FormBuilder,
    private lotPlanService: LotPlanService,
    private vwds: VegWizardDataService,
    private logger: Logger,
    router: Router) {
    super();

    this.lotPlanData = this.vwds.getLotPlanModel() ?? new LotPlanModel();

    this.form = this.formBuilder.group({
      lot: [this.lotPlanData.lot, Validators.required],
      plan: [this.lotPlanData.plan, Validators.required]
    });
  }

  ngOnInit(): void {

    if (this.lotPlanData) {
      // force to re-load and retrieve lotplan confirmation data when previous lot plan has been selected
      this.verify();
    }

    // ensure lot plan verification is set to false when either lot or plan changes.
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.lotPlanData = new LotPlanModel();
      this.isVerifyClicked = false;
      this.showLotPlanInformation = false;
      this.allowToGoNextScreen = false;
    });
  }

  verify() {
    this.isLoading = true;

    this.getLotOnPlan$()
      .pipe(first(), untilDestroyed(this))
      .subscribe(() => {
        this.isLoading = false;

        this.allowToGoNextScreen = this.lotPlanData.tenureIsValid
          && (!this.showNoCategoriesWarning() && !this.showNoValidCategoriesWarning())
          && (this.lotPlanData.lotOnPlanValid && this.isVerifyClicked);

        this.showLotPlanInformation = this.lotPlanData.tenureIsValid
          && (!this.showNoCategoriesWarning() && !this.showNoValidCategoriesWarning())
          && this.lotPlanData.lotOnPlanValid && this.isVerifyClicked;
      });
  }

  /**
   * Verifies the lot and plan entered by the user, and if valid will
   * save that lot plan data in the session.
   * @returns
   */
  getLotOnPlan$(): Observable<boolean> {

    const lot = this.form.get('lot')?.value;
    const plan = this.form.get('plan')?.value;

    if (!lot || !plan) { return of(false); }

    return this.lotPlanService.getLotPlan$(lot, plan)
      .pipe(
        mergeMap(results => {
          this.isVerifyClicked = true;
          this.lotPlanData = results;
          return results.lotOnPlanValid
            ? this.vwds.setLotOnPlanModel$(this.lotPlanData)
            : of(false);
        }),
        catchError((err: HttpErrorResponse) => {
          this.logger.error('getLotOnPlan error', err);
          return of(false)
        }));
  }

  showNoCategoriesWarning(): boolean {
    return this.lotPlanData.lotOnPlanValid &&
      this.lotPlanData.tenureIsValid &&
      !!this.lotPlanData.categories &&
      this.lotPlanData.categories.length == 0;
  }

  showNoValidCategoriesWarning(): boolean {
    return this.lotPlanData.lotOnPlanValid &&
      this.lotPlanData.tenureIsValid &&
      !this.lotPlanData.hasValidCategories;
  }

  override next(): void {
    if (!this.form.valid || !this.lotPlanData.lotOnPlanValid) {
      this.logger.warning('Lot plan form cannot continue, there were some validation issues!')
      return;
    }

    super.next();

  }

  override cancel(): void {
    super.cancel();
    this.vwds.clear().pipe(first(), untilDestroyed(this)).subscribe();
  }
}
